import React, { useMemo, useRef } from "react"
import JoditEditor from "jodit-react"
import { API_BASE_MEDIA_URL } from "src/config"
import { useUploadFile } from "src/hooks/use-upload-file"
import styled from "styled-components"

/** Above Ant Drawer (1000) and Select/Dropdown (1050). */
const JODIT_Z_INDEX = 10000

function getJoditPopupRoot() {
    if (typeof document !== "undefined") {
        return document.body
    }
    return null
}

function buildUploadedFileUrl(uploadedFile) {
    if (!uploadedFile?.path || !uploadedFile?.systemName) {
        return ""
    }

    const base = String(API_BASE_MEDIA_URL || "").replace(/\/+$/, "")
    const path = String(uploadedFile.path).replace(/^\/+/, "")
    const systemName = String(uploadedFile.systemName).replace(/^\/+/, "")

    return `${base}/${path}/${systemName}`
}

function mapUploadToJoditResponse(uploadedFile) {
    const url = buildUploadedFileUrl(uploadedFile)

    if (!url) {
        return {
            files: [],
            path: "",
            baseurl: "",
            error: 1,
            msg: "Не удалось получить URL загруженного файла",
        }
    }

    return {
        files: [url],
        path: "",
        baseurl: "",
        error: 0,
        msg: "",
    }
}

function getFileFromFormData(formData) {
    if (!(formData instanceof FormData)) {
        return null
    }

    const file = formData.get("file")
    if (file instanceof File) {
        return file
    }

    for (const [, value] of formData.entries()) {
        if (value instanceof File) {
            return value
        }
    }

    return null
}

async function uploadEditorImage(file) {
    const uploadedFile = await useUploadFile(file)

    if (!uploadedFile?.id) {
        throw new Error("Upload failed")
    }

    return mapUploadToJoditResponse(uploadedFile)
}

function createJoditCustomUpload() {
    return function (formData, showProgress) {
        const file = getFileFromFormData(formData)

        if (!file) {
            return Promise.resolve({
                success: false,
                time: "",
                data: {
                    messages: ["Файл не найден"],
                    files: [],
                    baseurl: "",
                },
            })
        }

        showProgress(10)

        return uploadEditorImage(file)
            .then((result) => {
                showProgress(100)

                if (result.error) {
                    return {
                        success: false,
                        time: "",
                        data: {
                            messages: [result.msg || "Ошибка загрузки"],
                            files: [],
                            baseurl: "",
                        },
                    }
                }

                return {
                    success: true,
                    time: new Date().toISOString(),
                    data: {
                        files: result.files,
                        baseurl: result.baseurl || "",
                        path: result.path || "",
                    },
                }
            })
            .catch((e) => {
                console.error("Image upload error", e)
                return {
                    success: false,
                    time: "",
                    data: {
                        messages: [e?.message || "Ошибка загрузки"],
                        files: [],
                        baseurl: "",
                    },
                }
            })
    }
}

export function TinyMCE({ value = "", onChange }) {
    const editorRef = useRef(null)

    const config = useMemo(() => {
        return {
            readonly: false,
            language: "ru",
            theme: "default",
            popupRoot: getJoditPopupRoot(),
            zIndex: JODIT_Z_INDEX,
            toolbarSticky: false,
            toolbarAdaptive: false,
            askBeforePasteHTML: false,
            askBeforePasteFromWord: false,
            defaultActionOnPaste: "insert_clear_html",
            enter: "P",
            buttons: [
                "paragraph",
                "fontsize",
                "brush",
                "|",
                "bold",
                "italic",
                "underline",
                "strikethrough",
                "|",
                "ul",
                "ol",
                "|",
                "outdent",
                "indent",
                "|",
                "left",
                "center",
                "right",
                "justify",
                "|",
                "link",
                "image",
                "table",
                "hr",
                "|",
                "superscript",
                "subscript",
                "blockquote",
                "|",
                "undo",
                "redo",
                "|",
                "eraser",
                "fullsize",
                "preview",
                "source",
            ],
            controls: {
                fontsize: {
                    list: {
                        "10px": "10px",
                        "12px": "12px",
                        "14px": "14px",
                        "16px": "16px",
                        "18px": "18px",
                        "20px": "20px",
                        "22px": "22px",
                        "24px": "24px",
                        "28px": "28px",
                        "32px": "32px",
                    },
                },
                paragraph: {
                    list: {
                        p: "Paragraph",
                        h2: "Heading 2",
                        h3: "Heading 3",
                        h4: "Heading 4",
                        h5: "Heading 5",
                        h6: "Heading 6",
                    },
                },
            },
            uploader: {
                insertImageAsBase64URI: false,
                imagesExtensions: ["jpg", "jpeg", "png", "gif", "webp"],
                url: "",
                filesVariableName: function () {
                    return "file"
                },
                isSuccess: function (resp) {
                    return Boolean(resp?.success)
                },
                process: function (resp) {
                    return resp?.data ?? resp
                },
                customUploadFunction: createJoditCustomUpload(),
                defaultHandlerSuccess: function (data) {
                    const jodit = this.j || this
                    if (data?.files?.length) {
                        data.files.forEach((url) => {
                            jodit.selection.insertImage(url)
                        })
                    }
                },
                error: function (e) {
                    console.error("Image upload error", e)
                },
            },
            events: {
                afterInit: function (editor) {
                    editor.o.popupRoot = getJoditPopupRoot()
                    editor.o.zIndex = JODIT_Z_INDEX
                },
                keydown: function (event) {
                    if (event.key === "Escape") {
                        const editor = this
                        editor.s.insertHTML("<p><br></p>")
                    }
                },
            },
            cleanHTML: {
                removeEmptyElements: false,
            },
            allowTags: {
                p: true,
                h2: true,
                h3: true,
                h4: true,
                h5: true,
                h6: true,
                blockquote: true,
                ul: true,
                ol: true,
                li: true,
                table: true,
                tbody: true,
                tr: true,
                td: true,
                th: true,
                a: {
                    href: true,
                    target: true,
                    rel: true,
                },
                img: {
                    src: true,
                    alt: true,
                    width: true,
                    height: true,
                    class: true,
                },
                noindex: true,
            },
            style: {
                fontSize: "18px",
                lineHeight: "1.3",
                color: "#494B52",
            },
        }
    }, [])

    return (
        <StyledEditor
            ref={editorRef}
            value={value}
            config={config}
            onBlur={(newContent) => onChange?.(newContent)}
        />
    )
}


const StyledEditor = styled(JoditEditor)`
  position: relative;
  z-index: 0;
  isolation: isolate;

  iframe {
    width: 100%;
    aspect-ratio: 9 / 16;
    height: auto;
    border: 0;
    display: block;
  }
`